/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import {
  useIntercom,
  IntercomProvider,
  IntercomProps,
} from "react-use-intercom";
import { createState } from "src/utils/create_zustand_store";
import { useRouterExt } from "../hooks/useRouterExt";
import { useTeamConfig } from "../components/TeamConfigProvider";
import { useSSAuth } from "../hooks/useSSAuth";
import { useRouterEvent } from "../hooks/useRouterEvent";
import { constants } from "src/shared/constants";
import { Box } from "@mui/material";

const APP_ID = "xol8gfkc";

export interface IntercomCtxType {
  intercom: ReturnType<typeof useIntercom>;
  bootIntercom: () => void;
  updateIntercom: (options: IntercomProps) => void;
  rebootIntercom: () => void;
  intercomState: ReturnType<typeof useIntercomState>;
}

export interface IntercomDataType {
  userId?: string;
  email?: string;
  customAttributes?: {
    accountId?: string;
    currentUrl?: string;
    marketCode?: string;
  };
}

export const useIntercomState = createState({
  isInitialized: false,
  intercomData: {} as IntercomDataType,
}); // intercom internal state

const IntercomCtx = React.createContext<IntercomCtxType | null>(null); // context

export const useIntercomCtx = () => {
  return React.useContext(IntercomCtx) as IntercomCtxType;
}; // context hook

function IntercomCtxWrapper(props: { children: React.ReactNode }) {
  const intercom = useIntercom();
  const { TEAM_SCHEMA, TEAM_GLOBAL_SETTINGS } = useTeamConfig();
  const { user } = useSSAuth();
  const intercomState = useIntercomState();
  const {
    isInitialized,
    intercomData,
    set: setIntercomState,
    reset: resetIntercomState,
  } = intercomState;

  /*
   +------------------------------------------------------------------------------+
   * [ intercom utilities ]
   +------------------------------------------------------------------------------+ */
  const bootIntercom = () => {
    intercom.boot();
    setIntercomState({ isInitialized: true });
  };

  const updateIntercom = (options: IntercomProps) => {
    intercom.update(options);
  };

  const rebootIntercom = () => {
    intercom.shutdown();
    bootIntercom();
  };

  const setIntercomData = (data: Partial<IntercomDataType>) => {
    setIntercomState({
      intercomData: {
        ...intercomData,
        ...data,
        customAttributes: {
          ...intercomData?.customAttributes,
          ...data?.customAttributes,
        },
      },
    });
  };

  // Initializing intercom
  useEffect(() => {
    if (TEAM_GLOBAL_SETTINGS?.IS_INTERCOM_ENABLED && !isInitialized) {
      bootIntercom();
      const currentUrl = window.location.href;
      setIntercomData({ customAttributes: { currentUrl } });
    }
  }, []);

  // updating intercom session with intercomData state
  React.useEffect(() => {
    if (isInitialized && intercomData) {
      updateIntercom(intercomData);
    }
  }, [intercomData, isInitialized]);

  // subscribing to router events and update intercom data state
  useRouterEvent({
    type: "routeChangeComplete",
    callback: () => {
      const currentUrl = window.location.href;
      setIntercomData({ customAttributes: { currentUrl } });
    },
  });

  // updating user data to the intercom data state
  useEffect(() => {
    if (user && isInitialized) {
      setIntercomData({
        email: user?.email,
        userId: `${TEAM_SCHEMA}-${String(user?.userId)}`,
        customAttributes: {
          accountId: user?.accountId,
        },
      });
    }
  }, [isInitialized]);

  // handling user logout and rebooting intercom session
  useEffect(() => {
    const handleUserLogout = () => {
      if (TEAM_GLOBAL_SETTINGS?.IS_INTERCOM_ENABLED) {
        resetIntercomState();
        intercom?.shutdown?.();
        intercom?.boot?.();
      }
    };
    window.addEventListener(
      constants.customEvents.userLogout,
      handleUserLogout
    );
    return () => {
      window.removeEventListener(
        constants.customEvents.userLogout,
        handleUserLogout
      );
    };
  }, []);

  return (
    <IntercomCtx.Provider
      value={{
        intercom,
        intercomState,
        bootIntercom,
        updateIntercom,
        rebootIntercom,
      }}
    >
      {props.children}
    </IntercomCtx.Provider>
  );
}

export const IntercomCtxProvider = (props: { children: React.ReactNode }) => (
  <IntercomProvider appId={APP_ID}>
    <IntercomCtxWrapper>{props.children}</IntercomCtxWrapper>
  </IntercomProvider>
);
